import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { 
  Spin, 
  Alert, 
  Card, 
  Typography, 
  Button, 
  Rate, 
  Input, 
  Checkbox, 
  Form, 
  message,
  Upload
} from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { 
  LikeOutlined, 
  DislikeOutlined,
  StarOutlined,
  CheckCircleOutlined,
  GoogleOutlined,
  PlusOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { env } from '../config/environment'
import { GoogleStyleReviewForm } from '../components/review/GoogleStyleReviewForm'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

// Use centralized environment config
const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

interface ReviewPageData {
  reviewId: string
  type: 'business_customer' | 'serviceflow_product'
  businessId: string
  businessName: string
  businessLogo: string | null
  businessPrimaryColor: string | null
  googleReviewUrl: string | null
  customerFirstName: string | null
  jobTitle: string | null
  sentiment: string | null
  isCompleted: boolean
  reviewerName: string | null
  reviewerTitle: string | null
  reviewerCompany: string | null
  reviewerImageUrl: string | null
}

type ViewState = 'loading' | 'error' | 'sentiment' | 'feedback' | 'testimonial' | 'thank-you' | 'google-redirect'

function trackGoogleReviewClick(token: string): void {
  fetch(`${API_URL}/api/public/review/${token}/google-clicked`, { method: 'POST' }).catch((err) => {
    console.error('Error tracking Google review click:', err)
  })
}

function redirectToGoogleReview(url: string): boolean {
  try {
    window.location.assign(url)
    return true
  } catch (err) {
    console.error('Error redirecting to Google review:', err)
    return false
  }
}

export const ReviewFeedback: React.FC = () => {
  const { token } = useParams()
  const [form] = Form.useForm()
  
  const [viewState, setViewState] = useState<ViewState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<ReviewPageData | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [reviewerImageUrl, setReviewerImageUrl] = useState<string | null>(null)
  const [fileList, setFileList] = useState<UploadFile[]>([])

  // Fetch review data on mount
  useEffect(() => {
    const fetchReviewData = async () => {
      if (!token) {
        setError('No review link provided')
        setViewState('error')
        return
      }

      try {
        const response = await fetch(`${API_URL}/api/public/review/${token}`)
        const result = await response.json()

        if (!result.success) {
          setError(result.error || 'Failed to load review page')
          setViewState('error')
          return
        }

        setData(result.data)

        if (result.data.reviewerImageUrl) {
          setReviewerImageUrl(result.data.reviewerImageUrl)
          setFileList([{
            uid: '-1',
            name: 'photo',
            status: 'done',
            url: result.data.reviewerImageUrl
          }])
        }

        form.setFieldsValue({
          reviewerName: result.data.reviewerName || result.data.customerFirstName || '',
          reviewerTitle: result.data.reviewerTitle || '',
          reviewerCompany: result.data.reviewerCompany || ''
        })
        
        // Determine initial view based on review state
        if (result.data.isCompleted) {
          setViewState('thank-you')
        } else if (result.data.type === 'serviceflow_product') {
          setViewState('testimonial')
        } else if (result.data.sentiment === 'negative') {
          setViewState('feedback')
        } else if (result.data.sentiment === 'positive') {
          if (result.data.googleReviewUrl) {
            trackGoogleReviewClick(token)
            if (!redirectToGoogleReview(result.data.googleReviewUrl)) {
              setViewState('google-redirect')
            }
          } else {
            setViewState('thank-you')
          }
        } else {
          setViewState('sentiment')
        }
      } catch (err) {
        console.error('Error fetching review data:', err)
        setError('Failed to load review page. Please try again.')
        setViewState('error')
      }
    }

    fetchReviewData()
  }, [token])

  // Handle thumbs up/down selection
  const handleSentimentSelect = async (sentiment: 'positive' | 'negative') => {
    if (!token) return

    setSubmitting(true)
    try {
      const response = await fetch(`${API_URL}/api/public/review/${token}/sentiment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sentiment })
      })

      const result = await response.json()
      if (!result.success) {
        message.error(result.error || 'Failed to record feedback')
        setSubmitting(false)
        return
      }

      if (sentiment === 'positive') {
        if (data?.googleReviewUrl) {
          trackGoogleReviewClick(token)
          if (!redirectToGoogleReview(data.googleReviewUrl)) {
            setViewState('google-redirect')
          }
        } else {
          setViewState('thank-you')
        }
      } else {
        setViewState('feedback')
      }
    } catch (err) {
      console.error('Error recording sentiment:', err)
      message.error('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Handle feedback form submission
  const handleFeedbackSubmit = async (values: any) => {
    if (!token) return

    setSubmitting(true)
    try {
      const payload: Record<string, unknown> = {
        rating: values.rating,
        feedback: values.feedback,
        publicReview: values.publicReview,
        reviewerName: values.reviewerName,
        reviewerImageUrl: reviewerImageUrl,
        isPublic: values.isPublic || false
      }

      if (data?.type === 'serviceflow_product') {
        payload.reviewerTitle = values.reviewerTitle
        payload.reviewerCompany = values.reviewerCompany
      }

      const response = await fetch(`${API_URL}/api/public/review/${token}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const result = await response.json()
      if (!result.success) {
        message.error(result.error || 'Failed to submit feedback')
        setSubmitting(false)
        return
      }

      setViewState('thank-you')
    } catch (err) {
      console.error('Error submitting feedback:', err)
      message.error('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReviewImageUpload = async (file: File) => {
    if (!token) return false

    setUploadingImage(true)
    try {
      const formData = new FormData()
      formData.append('image', file)

      const response = await fetch(`${API_URL}/api/public/review/${token}/upload`, {
        method: 'POST',
        body: formData
      })

      const result = await response.json()
      if (!result.success) {
        message.error(result.error || 'Failed to upload image')
        return false
      }

      setReviewerImageUrl(result.url)
      setFileList([{
        uid: file.name,
        name: file.name,
        status: 'done',
        url: result.url
      }])
      message.success('Photo uploaded')
    } catch (err) {
      console.error('Error uploading review image:', err)
      message.error('Failed to upload image')
    } finally {
      setUploadingImage(false)
    }

    return false
  }

  // Get primary color or default
  const primaryColor = data?.businessPrimaryColor || '#1890ff'
  const gradientStyle = `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, -30)} 100%)`

  // Render loading state
  if (viewState === 'loading') {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24
      }}>
        <Spin size="large" />
        <Text style={{ color: 'white', marginTop: 16 }}>Loading...</Text>
      </div>
    )
  }

  // Render error state
  if (viewState === 'error') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24
      }}>
        <Card style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <Alert 
            type="error" 
            message="Review Link Unavailable" 
            description={error}
            showIcon
          />
        </Card>
      </div>
    )
  }

  // Render sentiment selection (thumbs up/down)
  if (viewState === 'sentiment') {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: gradientStyle,
        padding: '24px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ maxWidth: 450, width: '100%' }}>
          {/* Business Logo */}
          {data?.businessLogo && (
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <img 
                src={data.businessLogo}
                alt={data.businessName}
                style={{ maxHeight: 60, maxWidth: 200 }}
              />
            </div>
          )}

          <Card 
            style={{ 
              borderRadius: 16, 
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              textAlign: 'center'
            }}
          >
            <div style={{ padding: '20px 0' }}>
              {/* Greeting */}
              <Title level={3} style={{ marginBottom: 8 }}>
                {data?.customerFirstName 
                  ? `Hi ${data.customerFirstName}!` 
                  : 'Hello!'}
              </Title>
              
              <Paragraph style={{ fontSize: 16, color: '#666', marginBottom: 32 }}>
                How was your experience with{' '}
                <strong>{data?.businessName || 'us'}</strong>
                {data?.jobTitle && (
                  <span> for your {data.jobTitle.toLowerCase()}</span>
                )}
                ?
              </Paragraph>

              {/* Thumbs Up / Thumbs Down Buttons */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 24,
                marginBottom: 16
              }}>
                <Button
                  type="default"
                  size="large"
                  onClick={() => handleSentimentSelect('positive')}
                  loading={submitting}
                  style={{ 
                    width: 140,
                    height: 140,
                    borderRadius: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #52c41a',
                    transition: 'all 0.3s ease'
                  }}
                  className="review-btn-positive"
                >
                  <LikeOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                  <span style={{ marginTop: 8, fontWeight: 600, color: '#52c41a' }}>
                    Great!
                  </span>
                </Button>

                <Button
                  type="default"
                  size="large"
                  onClick={() => handleSentimentSelect('negative')}
                  loading={submitting}
                  style={{ 
                    width: 140,
                    height: 140,
                    borderRadius: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #ff4d4f',
                    transition: 'all 0.3s ease'
                  }}
                  className="review-btn-negative"
                >
                  <DislikeOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
                  <span style={{ marginTop: 8, fontWeight: 600, color: '#ff4d4f' }}>
                    Not Great
                  </span>
                </Button>
              </div>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Your feedback helps us improve
              </Text>
            </div>
          </Card>

          {/* Footer */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: 24,
            color: 'rgba(255,255,255,0.8)',
            fontSize: 12
          }}>
            Powered by {data?.businessName}
          </div>
        </div>
      </div>
    )
  }

  // Render ServiceFlow product testimonial form
  if (viewState === 'testimonial') {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: gradientStyle,
        padding: '24px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ maxWidth: 520, width: '100%' }}>
          <Card 
            style={{ 
              borderRadius: 16, 
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={3} style={{ marginBottom: 8 }}>
                Share Your ServiceFlow Pro Experience
              </Title>
              <Paragraph style={{ color: '#666' }}>
                Tell us how ServiceFlow Pro has helped your business
              </Paragraph>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleFeedbackSubmit}
              initialValues={{
                reviewerName: data?.reviewerName || data?.customerFirstName || '',
                reviewerTitle: data?.reviewerTitle || '',
                reviewerCompany: data?.reviewerCompany || ''
              }}
            >
              <Form.Item
                name="rating"
                label="Overall rating"
                rules={[{ required: true, message: 'Please select a rating' }]}
              >
                <Rate style={{ fontSize: 32 }} character={<StarOutlined />} />
              </Form.Item>

              <Form.Item
                name="publicReview"
                label="Your testimonial"
                rules={[{ required: true, message: 'Please share your experience' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Tell us how ServiceFlow Pro has transformed your business..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item name="reviewerName" label="Your name">
                <Input placeholder="e.g., Sarah Johnson" />
              </Form.Item>

              <Form.Item name="reviewerTitle" label="Your title">
                <Input placeholder="e.g., Owner, Operations Manager" />
              </Form.Item>

              <Form.Item name="reviewerCompany" label="Company name">
                <Input placeholder="e.g., Johnson HVAC Services" />
              </Form.Item>

              <Form.Item label="Photo (optional)">
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  beforeUpload={(file) => {
                    handleReviewImageUpload(file as File)
                    return false
                  }}
                  onRemove={() => {
                    setReviewerImageUrl(null)
                    setFileList([])
                  }}
                  maxCount={1}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                >
                  {fileList.length >= 1 ? null : (
                    <div>
                      {uploadingImage ? <LoadingOutlined /> : <PlusOutlined />}
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <Form.Item
                name="feedback"
                label="Private feedback (optional)"
              >
                <TextArea
                  rows={3}
                  placeholder="Anything else you'd like us to know privately..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>

              <Form.Item name="isPublic" valuePropName="checked">
                <Checkbox>
                  Allow ServiceFlow Pro to display this review publicly (if approved)
                </Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  size="large"
                  block
                  style={{ height: 48, borderRadius: 8, background: primaryColor }}
                >
                  Submit Review
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </div>
    )
  }

  // Render feedback form (for negative sentiment — business customer reviews)
  if (viewState === 'feedback') {
    return (
      <GoogleStyleReviewForm
        form={form}
        businessName={data?.businessName || 'Business'}
        businessLogo={data?.businessLogo ?? null}
        customerFirstName={data?.customerFirstName ?? null}
        submitting={submitting}
        uploadingImage={uploadingImage}
        fileList={fileList}
        onFinish={handleFeedbackSubmit}
        onImageUpload={handleReviewImageUpload}
        onImageRemove={() => {
          setReviewerImageUrl(null)
          setFileList([])
        }}
      />
    )
  }

  // Fallback when automatic Google redirect fails
  if (viewState === 'google-redirect') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        padding: '24px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ maxWidth: 450, width: '100%' }}>
          <Card style={{ borderRadius: 8, textAlign: 'center' }}>
            <div style={{ padding: '24px 0' }}>
              <GoogleOutlined style={{ fontSize: 48, color: '#4285f4', marginBottom: 16 }} />
              <Title level={4} style={{ marginBottom: 8 }}>
                Leave a Google Review
              </Title>
              <Paragraph style={{ color: '#5f6368', marginBottom: 24 }}>
                Tap below to open Google and share your experience.
              </Paragraph>
              {data?.googleReviewUrl && (
                <Button
                  type="primary"
                  href={data.googleReviewUrl}
                  size="large"
                  style={{ background: '#1a73e8', borderColor: '#1a73e8', borderRadius: 20 }}
                >
                  Open Google Review
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    )
  }

  // Render thank you page
  if (viewState === 'thank-you') {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: gradientStyle,
        padding: '24px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ maxWidth: 450, width: '100%' }}>
          {/* Business Logo */}
          {data?.businessLogo && (
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <img 
                src={data.businessLogo}
                alt={data.businessName}
                style={{ maxHeight: 60, maxWidth: 200 }}
              />
            </div>
          )}

          <Card 
            style={{ 
              borderRadius: 16, 
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              textAlign: 'center'
            }}
          >
            <div style={{ padding: '30px 0' }}>
              <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
              <Title level={3} style={{ marginBottom: 8 }}>
                Thank You!
              </Title>
              <Paragraph style={{ fontSize: 16, color: '#666' }}>
                Your feedback has been submitted.
              </Paragraph>
              <Paragraph style={{ color: '#888' }}>
                We appreciate you taking the time to share your experience with{' '}
                <strong>{data?.businessName}</strong>.
              </Paragraph>
            </div>
          </Card>

          {/* Footer */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: 24,
            color: 'rgba(255,255,255,0.8)',
            fontSize: 12
          }}>
            Powered by {data?.businessName}
          </div>
        </div>
      </div>
    )
  }

  return null
}

// Helper function to darken/lighten a hex color
function adjustColor(color: string, amount: number): string {
  const clamp = (val: number) => Math.min(255, Math.max(0, val))
  
  // Remove # if present
  const hex = color.replace('#', '')
  
  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  
  // Adjust
  const newR = clamp(r + amount)
  const newG = clamp(g + amount)
  const newB = clamp(b + amount)
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}
